import React, { ReactElement, useCallback } from 'react';
import { RenderItem } from './RenderItem';
import { GridCardItem } from './GridCardItem';
import { SupportedCardGalleryItemProps } from './SupportedCardGalleryItemProps';
import { GridState } from '../../state';
import { AddItemProps } from '@wix/design-system';
import { ActionsCell } from '../ActionsCell';
import { DragAndDrop } from '../DragAndDrop';
import { FiltersMap } from '@wix/bex-core';
import { FolderIcon } from '../FolderIcon';

export interface UseRenderFolderItemParams<T, F extends FiltersMap> {
  folderPreset?: 'full' | 'title';

  renderFolderItem?: (
    item: T,
    index: number,
  ) => Partial<SupportedCardGalleryItemProps>;

  state: GridState<T, F>;

  renderFolderAddItem?: () => ReactElement<AddItemProps>;

  folderActionsCell?: ActionsCell<T, F>;

  dragAndDrop?: DragAndDrop;
}

export function useRenderFolderItem<T, F extends FiltersMap>(
  params: UseRenderFolderItemParams<T, F>,
) {
  const {
    state,
    folderPreset = 'title',
    renderFolderItem,
    dragAndDrop,
    folderActionsCell,
  } = params;

  return useCallback<RenderItem>(
    (index, { itemKey: _itemKey, index2d } = {}) => {
      const renderType = state.getItemRenderTypeAt(index);

      if (renderType === null) {
        return null;
      }

      if (renderType === 'trailing') {
        return null;
      }

      let child = (
        <GridCardItem
          state={state}
          index={index}
          rowIndex={index2d?.rowIndex}
          columnIndex={index2d?.columnIndex}
          keyedItem={renderType}
          renderItem={(item, index) => ({
            backgroundImageNode: <FolderIcon />,
            imagePlacement: 'side',
            ...renderFolderItem?.(item, index),
          })}
          preset={folderPreset}
          actionsCell={folderActionsCell}
        />
      );

      if (dragAndDrop) {
        const { DraggableCard } = dragAndDrop;
        child = (
          <DraggableCard
            id={renderType.key}
            index={renderType.index}
            state={state}
          >
            {child}
          </DraggableCard>
        );
      }

      return child;
    },
    [dragAndDrop?.DraggableCard],
  );
}
